import React from 'react';
import MuiPagination from '@mui/material/Pagination';
import {styled} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type {PaginationProps as MuiPaginationProps, PaginationRenderItemParams} from '@mui/material/Pagination';
import {PaginationItem} from './PaginationItem';
import {excludeComponentProps} from '../utils/excludeComponentProps';

type BasePaginationProps = Omit<MuiPaginationProps, 'variant' | 'shape' | 'color' | 'hideNextButton' | 'hidePrevButton' | 'siblingCount' | 'renderItem'>;

export interface PaginationProps extends BasePaginationProps {
    /**
     * Mobile breakpoint at which pages
     * and ellipsis are no longer visible
     * @default 700
     */
    breakpoint?: number;
    /**
     * To allow for
     * translations of the word
     * 'of' in the mobile view.
     */
    mobileText?: string;
}

const StyledPagination = styled(MuiPagination, {
    ...excludeComponentProps('breakpoint', 'mobileText')
})<PaginationProps>(({theme}) => ({
    '& .mobileDisplay': {
        color: theme.tokens.FuelColorTextTertiary,
        padding: `${theme.tokens.FuelSpacing1} ${theme.tokens.FuelSpacing2}`,
        fontSize: theme.tokens.FuelFontSizeSm
    },
    '& .MuiPaginationItem-root': {
        backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultBase,
        borderColor: theme.tokens.FuelColorBorderDark,
        borderRadius: theme.tokens.FuelBorderRadiusBase,
        color: theme.tokens.FuelColorTextTertiary,
        margin: `0px ${theme.tokens.FuelSpacing1}`,
        '&:hover': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultHovered,
            borderColor: theme.tokens.FuelColorBorderDark,
            color: theme.tokens.FuelColorTextTertiary
        },
        '&:active': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultPressed,
            borderColor: theme.tokens.FuelColorBorderDark,
            color: theme.tokens.FuelColorTextTertiary
        },
        '&.Mui-focusVisible': {
            boxShadow: theme.tokens.FuelShadowFocused,
            backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultBase
        },
        '&.Mui-selected': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultSelected,
            borderColor: theme.tokens.FuelColorBorderSelected,
            color: theme.tokens.FuelColorBrandPrimary,
            '&:hover': {
                backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultSelected
            }
        },
        '& .Mui-disabled': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultBase,
            borderColor: theme.tokens.FuelColorBorderDark,
            color: theme.tokens.FuelColorGray700,
            opacity: '0.5',
            pointerEvents: 'auto'
        },
        '&.MuiPaginationItem-sizeSmall': {
            minWidth: '2rem',
            height: '2rem',
            fontSize: theme.tokens.FuelFontSizeSm
        },
        '&.MuiPaginationItem-sizeMedium': {
            minWidth: '2.25rem',
            height: '2.25rem',
            fontSize: theme.tokens.FuelFontSizeSm
        },
        '&.MuiPaginationItem-sizeLarge': {
            minWidth: '2.5rem',
            height: '2.5rem',
            fontSize: theme.tokens.FuelFontSizeMd
        },
        '&.MuiPaginationItem-ellipsis': {
            size: theme.tokens.FuelFontSizeXs,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: 'transparent'
            }
        }
    }
}));

export const Pagination = React.forwardRef<any, PaginationProps>(
    ({
        breakpoint = 700,
        count = 10,
        mobileText = 'of',
        ...props
    }, ref) => {
        const headerBreakpointActive = useMediaQuery(`(max-width:${breakpoint}px)`);

        const renderPaginationItem = (item: PaginationRenderItemParams): React.ReactElement | undefined => {
            if (headerBreakpointActive) {
                if (item.type === 'page' && item.selected === true) {
                    return <div className='mobileDisplay'>{`${item.page} ${mobileText} ${count}`}</div>;
                }

                if (item.type === 'first' || item.type === 'next' || item.type === 'previous' || item.type === 'last') {
                    return (
                        <PaginationItem
                            className={item.type}
                            {...item}
                        />
                    );
                }

                return undefined;
            }

            return (
                <PaginationItem
                    className={item.type}
                    {...item}
                />
            );
        };

        return (
            <StyledPagination
                ref={ref}
                {...props}
                breakpoint={breakpoint}
                count={count}
                mobileText={mobileText}
                renderItem={renderPaginationItem}
                shape='rounded'
                siblingCount={1}
                variant='outlined'
            />
        );
    });

Pagination.displayName = 'Pagination';
